/* Stage 73.19 — Employee Custom CTC Bulk Upload UI.
 *
 * Lets an admin assign a per-employee custom salary bifurcation, bypassing
 * the standard CTC Group → Salary Structure path. Each row in the CSV is
 * one employee; columns are dynamically driven by the company's
 * `salary_heads` master, so whatever heads the company has defined (BASIC,
 * HRA, SPA, LTA, BONUS, FOOD_CARD, FUEL, PROF_DEV_ALLOWANCE, etc.) show up
 * as upload columns automatically.
 *
 * Flow:
 *   1. Pick company.
 *   2. (Optional) Download the pre-filled template.
 *   3. Edit CSV offline → upload.
 *   4. Pick mode: "update" replaces existing custom rows; "skip" leaves them.
 *   5. View success / skipped / failed report.
 *
 * Backend endpoints (already built):
 *   GET  /api/payroll/employee-ctc/bifurcated-template?company_id=…
 *   POST /api/payroll/employee-ctc/bifurcated-upload?company_id=…&mode=…
 *
 * Per the user's "Allow both, engine reads structure_source to decide" rule
 * (Stage 73.19 Option C), uploading a row here flips that employee to
 * `structure_source: "employee_custom"` and detaches `ctc_group_id`. The
 * Switch-Source endpoint can flip them back to Group later.
 *
 * Mounted at /payroll/employee-ctc-upload by App.js.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Download, Upload, FileSpreadsheet, CheckCircle2, XCircle,
  AlertTriangle, Info, ArrowLeft, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function EmployeeCTCBulkUpload() {
  const navigate = useNavigate();
  const { user, activeCompanyId } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [mode, setMode] = useState('update');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);
  const [file, setFile] = useState(null);
  const [counts, setCounts] = useState({ custom: 0, group: 0, total: 0 });
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Company list loading from authenticated context
  useEffect(() => {
    const authCompId = activeCompanyId || user?.active_company_id || user?.company_id || user?.tenant_id || user?.company?.id || '';
    const authCompName = user?.company_name || user?.company?.name || user?.tenant_name || user?.company_code || 'Company';

    if (isSuperAdmin) {
      axios.get(`${API_URL}/companies`, { withCredentials: true })
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.companies || []);
          setCompanies(list);
          const matched = list.find(c => (c.id || c._id) === authCompId);
          setCompanyId(matched ? (matched.id || matched._id) : (list[0]?._id || list[0]?.id || authCompId));
        })
        .catch(() => {
          if (authCompId) {
            setCompanies([{ _id: authCompId, id: authCompId, name: authCompName }]);
            setCompanyId(authCompId);
          }
        });
    } else {
      // Tenant Admin / Employee: automatically set logged-in company context from auth user
      if (authCompId) {
        setCompanies([{ _id: authCompId, id: authCompId, name: authCompName }]);
        setCompanyId(authCompId);
      }
    }
  }, [isSuperAdmin, user, activeCompanyId]);

  const loadCounts = useCallback(async () => {
    const targetCompId = companyId || user?.company_id || user?.tenant_id;
    setLoadingCounts(true);
    try {
      const url = targetCompId
        ? `${API_URL}/payroll/employee-ctc/source-summary?company_id=${targetCompId}`
        : `${API_URL}/payroll/employee-ctc/source-summary`;
      const res = await axios.get(url, { withCredentials: true });
      setCounts(res.data || { custom: 0, group: 0, total: 0 });
    } catch (_e) { /* non-blocking */ }
    setLoadingCounts(false);
  }, [companyId, user]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const downloadTemplate = async () => {
    const targetCompId = companyId || user?.company_id || user?.tenant_id;
    setDownloading(true);
    try {
      const url = targetCompId
        ? `${API_URL}/payroll/employee-ctc/bifurcated-template?company_id=${targetCompId}`
        : `${API_URL}/payroll/employee-ctc/bifurcated-template`;
      const res = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `employee_ctc_bifurcated_template_${targetCompId || 'tenant'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Download failed: ' + (e.response?.data?.detail || e.message));
    }
    setDownloading(false);
  };

  const uploadFile = async () => {
    const targetCompId = companyId || user?.company_id || user?.tenant_id;
    if (!file) { toast.error('Pick a CSV file first'); return; }
    setUploading(true);
    setReport(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const url = targetCompId
        ? `${API_URL}/payroll/employee-ctc/bifurcated-upload?company_id=${targetCompId}&mode=${mode}`
        : `${API_URL}/payroll/employee-ctc/bifurcated-upload?mode=${mode}`;
      const res = await axios.post(url, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(res.data);
      toast.success(res.data.message || 'Upload processed');
      loadCounts();
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
    }
    setUploading(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="employee-ctc-bulk-upload">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3"
        data-testid="ctc-bulk-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-violet-600" />
            Employee Custom CTC — Bulk Upload
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-3xl">
            Upload a per-employee salary bifurcation. The engine will bypass
            the CTC Group template for these employees and use the uploaded
            heads as-is. Use this for off-grade compensation, senior
            executives, or any edge case where the standard group breakdown
            does not fit.
          </p>
        </div>
      </div>

      {/* Source-of-truth banner — "no system confusion" rule. */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900 flex items-start gap-2" data-testid="ctc-source-rule-banner">
        <Info className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
        <div>
          <div className="font-semibold mb-0.5">Single source of truth — how the engine resolves CTC</div>
          For every employee, the engine reads <code className="bg-amber-100 px-1 rounded">structure_source</code> on
          their active <code className="bg-amber-100 px-1 rounded">employee_ctc</code> row:
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            <li><b>employee_custom</b> — uses the uploaded bifurcation (this page). Group ID is detached.</li>
            <li><b>group</b> — uses the CTC Group's salary structure template.</li>
          </ul>
          Only ONE source is active at a time. Uploading here flips affected employees to <b>employee_custom</b>;
          to revert, call the Switch-Source endpoint from the employee profile.
        </div>
      </div>

      {/* Arrivation logic banner — Stage 73.19b. Clear separation of Fixed CTC vs Variable MI. */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-4 text-xs text-violet-900" data-testid="ctc-arrivation-banner">
        <div className="font-semibold mb-1 flex items-center gap-1.5">
          <FileSpreadsheet className="w-4 h-4" /> Gross → Final CTC arrivation
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
          <div className="bg-white/60 rounded px-2 py-1.5 border border-violet-100">
            <div className="font-semibold text-violet-800">1 · Fixed CTC heads</div>
            <div className="text-violet-700">BASIC + HRA + SPA + CONV + MED + LTA + EXGR + BONUS + ELE …</div>
            <div className="text-violet-600 mt-0.5">→ Gross Annual</div>
          </div>
          <div className="bg-white/60 rounded px-2 py-1.5 border border-violet-100">
            <div className="font-semibold text-violet-800">2 · Perks + Employer side</div>
            <div className="text-violet-700">LTA perk, Employer PF, Employer ESI, Gratuity (when modelled)</div>
            <div className="text-violet-600 mt-0.5">→ Final CTC = Gross + Perks + Employer parts</div>
          </div>
          <div className="bg-white/60 rounded px-2 py-1.5 border border-violet-100">
            <div className="font-semibold text-rose-700">3 · NOT in this template</div>
            <div className="text-rose-600">INCENTIVE, OT, Arrears, Reimbursements — these are per-cycle inputs.</div>
            <div className="text-rose-500 mt-0.5">Go to Monthly Inputs screen instead.</div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-violet-200 text-[11px] text-violet-800">
          <b>Add a new salary head?</b> Open{' '}
          <a href="/masters?master=salary_heads" className="underline font-semibold hover:text-violet-600" data-testid="ctc-masters-link">
            Masters → Salary Heads
          </a>{' '}
          and toggle <b>Part of CTC = ON</b> when creating it. The template auto-includes every head with that flag on — no code change required.
        </div>
      </div>

      {/* Company picker */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
        {isSuperAdmin ? (
          <select
            value={companyId}
            onChange={e => setCompanyId(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm w-72"
            data-testid="ctc-bulk-company-select"
          >
            <option value="">— pick a company —</option>
            {companies.map(c => (
              <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            readOnly
            disabled
            value={companies[0]?.name || user?.company_name || user?.tenant_name || user?.company_code || 'Company'}
            className="border border-slate-200 bg-slate-100 rounded-md px-3 py-2 text-sm w-72 font-semibold text-slate-700 cursor-not-allowed"
            data-testid="ctc-bulk-company-select"
          />
        )}

        {companyId && (
          <div className="mt-3 flex items-center gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1.5" data-testid="ctc-count-group">
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">GROUP</span>
              {loadingCounts ? '…' : counts.group}
            </span>
            <span className="flex items-center gap-1.5" data-testid="ctc-count-custom">
              <span className="px-2 py-0.5 rounded bg-violet-100 text-violet-800 font-medium">CUSTOM</span>
              {loadingCounts ? '…' : counts.custom}
            </span>
            <span className="text-slate-400">·</span>
            <span data-testid="ctc-count-total">{counts.total} employees total</span>
            <button
              onClick={loadCounts}
              className="ml-2 text-slate-400 hover:text-slate-700"
              title="Refresh"
              data-testid="ctc-counts-refresh"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Step 1: Download template */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <span className="bg-violet-100 text-violet-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">1</span>
              Download CSV template
            </h2>
            <p className="text-xs text-slate-500 mt-1 ml-8">
              Template columns are driven by your company's salary heads master. Pre-fills up to 30 active employees with a suggested 50/25/25 BASIC/HRA/SPA split.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            disabled={!companyId || downloading}
            className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
            data-testid="ctc-bulk-download-template-btn"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download Template
          </button>
        </div>
      </div>

      {/* Step 2: Upload */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">
          <span className="bg-violet-100 text-violet-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">2</span>
          Upload completed CSV
        </h2>
        <div className="ml-8 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Mode</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="update"
                  checked={mode === 'update'}
                  onChange={() => setMode('update')}
                  data-testid="ctc-bulk-mode-update"
                />
                <span><b>Update</b> — replace existing custom rows</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="skip"
                  checked={mode === 'skip'}
                  onChange={() => setMode('skip')}
                  data-testid="ctc-bulk-mode-skip"
                />
                <span><b>Skip</b> — leave existing custom rows alone</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">CSV file</label>
            <input
              type="file"
              accept=".csv"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="text-xs"
              data-testid="ctc-bulk-file-input"
            />
            {file && <span className="ml-2 text-xs text-slate-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</span>}
          </div>
          <button
            onClick={uploadFile}
            disabled={!companyId || !file || uploading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
            data-testid="ctc-bulk-upload-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload & Process
          </button>
        </div>
      </div>

      {/* Step 3: Report */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ctc-bulk-report">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Upload Report</h2>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="ctc-bulk-success-card">
              <div className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Success
              </div>
              <div className="text-2xl font-bold text-emerald-900">{report.success?.length || 0}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3" data-testid="ctc-bulk-skipped-card">
              <div className="text-xs text-slate-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Skipped
              </div>
              <div className="text-2xl font-bold text-slate-700">{report.skipped?.length || 0}</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded p-3" data-testid="ctc-bulk-failed-card">
              <div className="text-xs text-rose-700 font-medium flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> Failed
              </div>
              <div className="text-2xl font-bold text-rose-900">{report.failed?.length || 0}</div>
            </div>
          </div>
          {report.success?.length > 0 && (
            <div className="mb-4" data-testid="ctc-bulk-success-list">
              <h3 className="text-xs font-semibold text-emerald-800 mb-1">Updated successfully</h3>
              <div className="overflow-x-auto max-h-60 overflow-y-auto border border-emerald-100 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-emerald-50 text-emerald-800 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Employee Code</th>
                      <th className="px-3 py-1.5 text-right">Gross Monthly</th>
                      <th className="px-3 py-1.5 text-right">CTC Annual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.success.map((s, i) => (
                      <tr key={i} className="border-t border-emerald-100">
                        <td className="px-3 py-1 font-mono">{s.employee_code}</td>
                        <td className="px-3 py-1 text-right font-mono">₹ {Number(s.gross_monthly).toLocaleString()}</td>
                        <td className="px-3 py-1 text-right font-mono">₹ {Number(s.ctc_annual).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {report.skipped?.length > 0 && (
            <div className="mb-4" data-testid="ctc-bulk-skipped-list">
              <h3 className="text-xs font-semibold text-slate-700 mb-1">Skipped</h3>
              <ul className="text-xs text-slate-600 list-disc ml-5 space-y-0.5">
                {report.skipped.map((s, i) => (
                  <li key={i}><span className="font-mono">{s.employee_code}</span> — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {report.failed?.length > 0 && (
            <div data-testid="ctc-bulk-failed-list">
              <h3 className="text-xs font-semibold text-rose-700 mb-1">Failed</h3>
              <ul className="text-xs text-rose-700 list-disc ml-5 space-y-0.5">
                {report.failed.map((s, i) => (
                  <li key={i}><span className="font-mono">{s.employee_code}</span> — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
